import React from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { ArrowUpRight, ChevronRight, BookOpen, FileText, HelpCircle, Building, Coins, ClipboardList, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ConsultationForm } from '../components/LeadForms';

// Knowledge Hub Content
const knowledgeContent = {
  'self-redevelopment': {
    title: 'What is Self Redevelopment?',
    icon: Building,
    shortDesc: 'Understanding the self-redevelopment process for housing societies.',
    content: [
      {
        type: 'paragraph',
        text: 'Self-redevelopment is a process where a housing society (Co-op Housing / CHS) redevelops its own building without appointing a private builder.'
      },
      {
        type: 'paragraph',
        text: 'Instead of giving the land to a builder (who normally takes most extra profits), the society becomes its own developer: it appoints architects, consultants, contractors, organizes approvals, arranges finance, and carries out project management itself.'
      },
      {
        type: 'paragraph',
        text: 'The benefits and additional profits stay with society members rather than going to a builder. Mumbai\'s methodology for redevelopment is governed by DCPR 2034 and supported by Government Resolutions (like the 2019 Self-Redevelopment GR) to encourage societies to undertake this route.'
      },
      {
        type: 'heading',
        text: 'Benefits of Self-Redevelopment'
      },
      {
        type: 'list',
        items: [
          'More control & benefits stay with society - Society decides on design, amenities, materials and contractors instead of a builder. Surplus from sale of extra flats goes directly to society members.',
          'Better financial returns - Members may get higher share of extra built-up area and higher corpus/surplus funds.',
          'Modern homes + higher property value - New building with modern safety standards, better utilities, lifts, parking, etc.',
          'Transparency & ownership rights remain - All documentation, plans and property rights stay under the society name.',
          'Government incentives - Concessions on premium, additional FSI percent, interest subsidies, and single-window approvals.'
        ]
      },
      {
        type: 'heading',
        text: 'Challenges of Self-Redevelopment'
      },
      {
        type: 'list',
        items: [
          'Requires strong internal coordination - Must achieve very high member consensus (often close to 100% consent).',
          'Financial responsibility - Society must arrange upfront funding — banks finance up to ~95% but society/members must secure the rest.',
          'Technical & administrative burden - Society must run or oversee all technical aspects.',
          'Time-consuming & risk of delays - More steps and internal management can stretch timelines.',
          'Eligibility limitations & bureaucracy - Some policies have very strict conditions.'
        ]
      }
    ]
  },
  '79a-process': {
    title: '79A Redevelopment Process',
    icon: ClipboardList,
    shortDesc: 'Step-by-step guide to the 79A directive redevelopment process.',
    content: [
      {
        type: 'paragraph',
        text: 'The 79A directive outlines the standard procedure for housing society redevelopment in Maharashtra. Here\'s a comprehensive step-by-step guide:'
      },
      {
        type: 'heading',
        text: 'Phase 1: Initial Steps (1-5)'
      },
      {
        type: 'steps',
        items: [
          { step: 1, title: 'Structural Audit Report', desc: 'Obtained from Licensed Structural Engineer' },
          { step: 2, title: 'Resolution for Redevelopment', desc: 'Passed by Managing Committee' },
          { step: 3, title: 'Fresh Elections', desc: 'Nomination for the MC/RDC' },
          { step: 4, title: 'M20 Form', desc: 'Submitted to Registrar by Managing Committee' },
          { step: 5, title: 'Consent', desc: 'Special General Body Meeting - Affidavit cum Declaration on Rs.100/- Stamp paper' }
        ]
      },
      {
        type: 'heading',
        text: 'Phase 2: Appointments & Documentation (6-11)'
      },
      {
        type: 'steps',
        items: [
          { step: 6, title: 'Appointment of Architect/PMC', desc: 'From minimum 3 quotations, announced in SGM' },
          { step: 7, title: 'Documentation', desc: 'City Survey Plan, DP Remarks, Carpet Areas, PR Card' },
          { step: 8, title: 'Feasibility Report', desc: 'Available for inspection to GB for at least 7 days' },
          { step: 9, title: 'Tender Document', desc: 'Copy circulated to GB for suggestions within 7 days' },
          { step: 10, title: 'Discussion/Recommendations', desc: 'Held in SGM' },
          { step: 11, title: 'Paper Ad', desc: 'Bidder invitation in at least 3 newspapers' }
        ]
      },
      {
        type: 'heading',
        text: 'Phase 3: Tender Process (12-18)'
      },
      {
        type: 'steps',
        items: [
          { step: 12, title: 'Release of Tender', desc: 'Collection with Non-Refundable Fees payable to Society' },
          { step: 13, title: 'Tender Clarification', desc: 'At least 3 days for queries' },
          { step: 14, title: 'Tender Submission', desc: 'Commercial & Technical Bid submission' },
          { step: 15, title: 'SGM', desc: 'Announcement of Commercial Offer' },
          { step: 16, title: 'Comparative Statement', desc: 'Submitted to MC & discussed in SGM' },
          { step: 17, title: 'Short Listing', desc: 'Presentations of Shortlisted Developers' },
          { step: 18, title: 'Elimination/Interview', desc: 'By MC/RDC' }
        ]
      },
      {
        type: 'heading',
        text: 'Phase 4: Final Selection (19-23)'
      },
      {
        type: 'steps',
        items: [
          { step: 19, title: 'Final Five', desc: 'Announce Final Developers & Final Presentations in SGM' },
          { step: 20, title: 'Site Visits/Presentation', desc: 'MC/RDC coordinates the Site Visits' },
          { step: 21, title: 'Grading', desc: 'Based on various parameters by Architect/PMC' },
          { step: 22, title: 'Informal Voting', desc: 'Preparation for final voting in front of Registrar' },
          { step: 23, title: 'Developer Appointment', desc: 'In presence of PMC, with video recording' }
        ]
      }
    ]
  },
  'self-redevelopment-procedure': {
    title: 'Self Redevelopment Procedure',
    icon: FileText,
    shortDesc: 'Detailed procedure for societies undertaking self-redevelopment.',
    content: [
      {
        type: 'heading',
        text: '1. Initial Decision & Resolution'
      },
      {
        type: 'list',
        items: [
          'Society convenes a Special General Body Meeting (SGM)',
          'Discuss self-redevelopment idea, explain benefits & costs',
          'Pass a resolution with required consent threshold (often 66% by share capital or full membership consent)'
        ]
      },
      {
        type: 'heading',
        text: '2. Appoint Professional Team'
      },
      {
        type: 'list',
        items: [
          'Project Management Consultant (PMC)',
          'Architect / Structural Consultant',
          'Legal Advisor',
          'Chartered Accountant (for financial viability)'
        ]
      },
      {
        type: 'paragraph',
        text: 'These experts prepare feasibility studies, projected costs, FSI benefits, and technical strategy.'
      },
      {
        type: 'heading',
        text: '3. Detailed Project Report'
      },
      {
        type: 'paragraph',
        text: 'PMC & architect prepare the DPR covering:'
      },
      {
        type: 'list',
        items: [
          'Building condition, structural audit',
          'Proposed building plan',
          'Construction cost estimate',
          'Extra saleable area and surplus fund projections',
          'Timelines & risk plan'
        ]
      },
      {
        type: 'heading',
        text: '4. Financing'
      },
      {
        type: 'list',
        items: [
          'Apply for bank loan / finance (up to ~95% of project cost)',
          'Society may need to mortgage land as security',
          'Members may contribute via booking extra flats or internal loans'
        ]
      },
      {
        type: 'heading',
        text: '5. Approvals & Permissions'
      },
      {
        type: 'list',
        items: [
          'MCGM / Municipal authority for Building Plan Approval (IOD/CC/OCs)',
          'Environmental, fire, water/sewer clearances',
          'RERA registration (for sale of extra flats)'
        ]
      },
      {
        type: 'heading',
        text: '6. Tendering & Contractor Selection'
      },
      {
        type: 'list',
        items: [
          'Issue tenders to contractors',
          'Evaluate based on experience, price, financial capacity',
          'Select best contractor with project timeline & quality clauses'
        ]
      },
      {
        type: 'heading',
        text: '7. Temporary Relocation'
      },
      {
        type: 'paragraph',
        text: 'Society members vacate existing building temporarily for safe demolition & construction. Society arranges rent or alternate accommodation if needed.'
      },
      {
        type: 'heading',
        text: '8. Demolition & Construction'
      },
      {
        type: 'list',
        items: [
          'Shift to alternate homes, demolish old building',
          'Construction begins as per approved plan',
          'PMC oversees quality, safety, schedule & payments'
        ]
      },
      {
        type: 'heading',
        text: '9. Completion & Occupation'
      },
      {
        type: 'list',
        items: [
          'Obtain Occupation Certificate (OC)',
          'Transfer new flats to members',
          'Distribute surplus corpus / extra benefits as per society policy'
        ]
      }
    ]
  },
  'funding-options': {
    title: 'Funding & Financial Options',
    icon: Coins,
    shortDesc: 'Various funding options available for redevelopment projects.',
    content: [
      {
        type: 'paragraph',
        text: 'Understanding the various funding options available for redevelopment projects is crucial for successful project execution.'
      },
      {
        type: 'heading',
        text: '1. Bank / Co-op Bank Loans'
      },
      {
        type: 'paragraph',
        text: 'Banks like Mumbai District Central Cooperative Bank may finance up to ~95% of project cost, including temporary rent costs. Society secures the rest.'
      },
      {
        type: 'heading',
        text: '2. Sale of Extra Flats'
      },
      {
        type: 'paragraph',
        text: 'Extra saleable flats (beyond existing members\' entitlement) — sold by the society — help repay loans and generate surplus.'
      },
      {
        type: 'heading',
        text: '3. Member Contributions'
      },
      {
        type: 'paragraph',
        text: 'Initial funding via members pre-booking extra area or contributions for start-up phase.'
      },
      {
        type: 'heading',
        text: '4. Construction Partner Funding'
      },
      {
        type: 'paragraph',
        text: 'Funding taken from the Construction Partner at the time of construction stage and repaying it by Sale of Extra flats or giving away Extra Sale Area as Barter to the Construction partner (With Interest).'
      },
      {
        type: 'heading',
        text: '5. Investor/Funding Partner'
      },
      {
        type: 'paragraph',
        text: 'Funding taken from the Investor/Funding Partner at the time of construction stage and repaying it by Sale of Extra flats or giving away Extra Sale Area as Barter to the Investor/Funding Partner (With Interest).'
      }
    ]
  },
  'pmc-role': {
    title: 'Role of PMC in Redevelopment',
    icon: HelpCircle,
    shortDesc: 'Understanding why PMC is essential for redevelopment projects.',
    content: [
      {
        type: 'paragraph',
        text: 'A Project Management Consultant (PMC) plays a critical role in ensuring that a housing redevelopment project is executed efficiently, transparently and in the best interests of the society members.'
      },
      {
        type: 'heading',
        text: '1. Expert Guidance from Start to Finish'
      },
      {
        type: 'paragraph',
        text: 'A PMC brings years of experience and technical expertise in:'
      },
      {
        type: 'list',
        items: [
          'Feasibility analysis',
          'Developer selection',
          'Regulatory compliance',
          'Legal & technical documentation'
        ]
      },
      {
        type: 'heading',
        text: '2. Transparent Developer Selection'
      },
      {
        type: 'paragraph',
        text: 'PMCs float tenders, analyze bids, and conduct negotiations to help the society select the most suitable and credible developer—not just the highest offer.'
      },
      {
        type: 'heading',
        text: '3. Protecting Society\'s Interests'
      },
      {
        type: 'paragraph',
        text: 'The PMC serves as an impartial representative, safeguarding the society\'s interests in all interactions with developers, architects, legal consultants, and municipal authorities.'
      },
      {
        type: 'heading',
        text: '4. Timeline and Cost Control'
      },
      {
        type: 'paragraph',
        text: 'Through structured project planning, milestone tracking, and rigorous financial oversight, PMCs ensure:'
      },
      {
        type: 'list',
        items: [
          'Timely delivery of possession',
          'Prevention of cost overruns',
          'Avoidance of unnecessary delays'
        ]
      },
      {
        type: 'heading',
        text: '5. Regulatory & Technical Oversight'
      },
      {
        type: 'paragraph',
        text: 'PMCs guarantee compliance and readiness by:'
      },
      {
        type: 'list',
        items: [
          'Aligning building plans with DCR norms',
          'Adhering to environmental, fire, and safety regulations',
          'Securing all necessary approvals before work commences'
        ]
      }
    ]
  },
  'society-guide': {
    title: 'Society Redevelopment Guide',
    icon: BookOpen,
    shortDesc: 'Complete guide for housing societies considering redevelopment.',
    content: [
      {
        type: 'paragraph',
        text: 'This comprehensive guide helps housing societies understand and navigate the redevelopment process from start to finish.'
      },
      {
        type: 'heading',
        text: 'Understanding Redevelopment'
      },
      {
        type: 'paragraph',
        text: 'Redevelopment involves demolishing an old building and constructing a new one with modern amenities, increased carpet area, and additional saleable space. The process can be undertaken through a developer or via self-redevelopment.'
      },
      {
        type: 'heading',
        text: 'Key Decisions for Societies'
      },
      {
        type: 'list',
        items: [
          'Developer Redevelopment vs Self-Redevelopment',
          'Selection of PMC and Architect',
          'Financing strategy',
          'Temporary relocation arrangements',
          'Timeline and quality expectations'
        ]
      },
      {
        type: 'heading',
        text: 'Government Regulations'
      },
      {
        type: 'paragraph',
        text: 'Key regulations governing redevelopment include:'
      },
      {
        type: 'list',
        items: [
          'DCPR 2034 (Development Control and Promotion Regulations)',
          '79A Directive for developer appointment',
          'Self-Redevelopment GR 2019',
          'RERA compliance requirements',
          'MHADA and BMC guidelines'
        ]
      },
      {
        type: 'heading',
        text: 'Financial Planning'
      },
      {
        type: 'paragraph',
        text: 'Societies must carefully plan finances considering:'
      },
      {
        type: 'list',
        items: [
          'Construction costs',
          'Temporary accommodation/rent',
          'Professional fees (PMC, Architect, Legal)',
          'Contingency fund',
          'Revenue from sale of extra flats'
        ]
      },
      {
        type: 'heading',
        text: 'Member Rights & Responsibilities'
      },
      {
        type: 'list',
        items: [
          'Right to information about all project details',
          'Voting rights in key decisions',
          'Responsibility to provide consent and cooperation',
          'Responsibility to pay dues on time'
        ]
      }
    ]
  }
};

const knowledgeTopics = [
  { id: 'self-redevelopment', ...knowledgeContent['self-redevelopment'] },
  { id: '79a-process', ...knowledgeContent['79a-process'] },
  { id: 'self-redevelopment-procedure', ...knowledgeContent['self-redevelopment-procedure'] },
  { id: 'funding-options', ...knowledgeContent['funding-options'] },
  { id: 'pmc-role', ...knowledgeContent['pmc-role'] },
  { id: 'society-guide', ...knowledgeContent['society-guide'] }
];

// Knowledge Hub Hero
const KnowledgeHubHero = () => {
  return (
    <section className="pt-[140px] pb-[120px] md:pt-[180px] bg-background" data-testid="knowledge-hub-hero">
      <div className="arch-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="text-sm uppercase tracking-widest text-gold mb-4">Knowledge Hub</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white mb-6 leading-tight">
            Redevelopment<br />
            <span className="text-gold">Knowledge Center</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Your comprehensive resource for understanding housing society redevelopment in Mumbai. 
            Learn about self-redevelopment, 79A process, funding options, and more.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Topics Grid
const TopicsGrid = () => {
  return (
    <section className="arch-section bg-card" data-testid="topics-grid">
      <div className="arch-container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {knowledgeTopics.map((topic, index) => (
            <motion.div
              key={topic.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Link
                to={`/knowledge-hub/${topic.id}`}
                className="block p-8 border border-white/10 bg-background hover:border-gold/30 transition-all group h-full"
                data-testid={`topic-card-${topic.id}`}
              >
                <topic.icon className="w-10 h-10 text-gold mb-6" strokeWidth={1.5} />
                <h3 className="text-xl font-serif text-white mb-3 group-hover:text-gold transition-colors">
                  {topic.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  {topic.shortDesc}
                </p>
                <span className="inline-flex items-center gap-2 text-gold text-sm">
                  Read More <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const KnowledgeHubCTA = () => {
  return (
    <section className="arch-section bg-background" data-testid="knowledge-hub-cta">
      <div className="arch-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-serif text-white mb-6">
              Need Expert Guidance?
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8">
              Our team of experienced professionals can help your society navigate the 
              complexities of redevelopment. Get a free assessment and personalized consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button
                  className="bg-gold text-black hover:bg-gold-light rounded-none px-6 py-5 text-xs uppercase tracking-widest font-bold"
                  data-testid="knowledge-cta-btn"
                >
                  Get Free Assessment
                  <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <ConsultationForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Main Knowledge Hub Page
const KnowledgeHubPage = () => {
  return (
    <main data-testid="knowledge-hub-page">
      <KnowledgeHubHero />
      <TopicsGrid />
      <KnowledgeHubCTA />
    </main>
  );
};

// Knowledge Article Page
export const KnowledgeArticlePage = () => {
  const { topicId } = useParams();
  const topic = knowledgeContent[topicId];

  if (!topic) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-4xl font-serif text-white mb-4">Article Not Found</h1>
          <Link to="/knowledge-hub">
            <Button variant="outline" className="border-white/20 text-white">
              Back to Knowledge Hub
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const renderContent = (item, index) => {
    switch (item.type) {
      case 'paragraph':
        return (
          <p key={index} className="text-muted-foreground leading-relaxed text-lg mb-6">
            {item.text}
          </p>
        );
      case 'heading':
        return (
          <h3 key={index} className="text-xl font-serif text-white mt-8 mb-4">
            {item.text}
          </h3>
        );
      case 'list':
        return (
          <ul key={index} className="space-y-3 mb-6">
            {item.items.map((listItem, i) => (
              <li key={i} className="flex items-start gap-3 text-muted-foreground">
                <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span>{listItem}</span>
              </li>
            ))}
          </ul>
        );
      case 'steps':
        return (
          <div key={index} className="space-y-4 mb-6">
            {item.items.map((step, i) => (
              <div key={i} className="flex gap-4 p-4 border border-white/10">
                <span className="w-8 h-8 bg-gold text-black flex items-center justify-center font-bold flex-shrink-0">
                  {step.step}
                </span>
                <div>
                  <h4 className="text-white font-medium">{step.title}</h4>
                  <p className="text-muted-foreground text-sm">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <main data-testid="knowledge-article-page">
      <section className="pt-[140px] pb-[60px] md:pt-[180px] bg-background">
        <div className="max-w-[1000px] mx-auto px-[40px]">
          <Link
            to="/knowledge-hub"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-gold transition-colors mb-8"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Back to Knowledge Hub
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <topic.icon className="w-12 h-12 text-gold mb-6" strokeWidth={1.5} />
            <h1 className="text-4xl md:text-5xl font-serif text-white mb-6">
              {topic.title}
            </h1>
            <p className="text-xl text-muted-foreground mb-12">
              {topic.shortDesc}
            </p>
          </motion.div>

          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-invert max-w-none"
          >
            {topic.content.map((item, index) => renderContent(item, index))}
          </motion.article>

          {/* Related Topics */}
          <div className="mt-16 pt-16 border-t border-white/10">
            <h3 className="text-xl font-serif text-white mb-6">Related Topics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {knowledgeTopics
                .filter(t => t.id !== topicId)
                .slice(0, 2)
                .map((relatedTopic) => (
                  <Link
                    key={relatedTopic.id}
                    to={`/knowledge-hub/${relatedTopic.id}`}
                    className="p-6 border border-white/10 hover:border-gold/30 transition-colors group"
                  >
                    <h4 className="text-white font-serif group-hover:text-gold transition-colors mb-2">
                      {relatedTopic.title}
                    </h4>
                    <p className="text-muted-foreground text-sm">{relatedTopic.shortDesc}</p>
                  </Link>
                ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 p-8 bg-card border border-white/10 text-center">
            <h3 className="text-2xl font-serif text-white mb-4">
              Need Expert Assistance?
            </h3>
            <p className="text-muted-foreground mb-6">
              Our team can help your society navigate the redevelopment process.
            </p>
            <Link to="/contact">
              <Button
                className="bg-gold text-black hover:bg-gold-light rounded-none px-8 py-6 text-sm uppercase tracking-widest font-bold"
              >
                Schedule Consultation
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default KnowledgeHubPage;
